import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// Toast Context
const ToastContext = createContext();

// Toast types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning'
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      duration
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, removeAllToasts }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2">
      {toasts.map(toast => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );
};

// Individual Toast Component
const ToastItem = ({ toast, onRemove }) => {
  const { message, type } = toast;

  const getToastStyles = () => {
    const baseStyles = "flex items-center p-4 rounded-lg shadow-lg max-w-sm w-full transform transition-all duration-300 ease-in-out";
    
    switch (type) {
      case TOAST_TYPES.SUCCESS:
        return `${baseStyles} bg-green-50 border border-green-200 text-green-800`;
      case TOAST_TYPES.ERROR:
        return `${baseStyles} bg-red-50 border border-red-200 text-red-800`;
      case TOAST_TYPES.WARNING:
        return `${baseStyles} bg-yellow-50 border border-yellow-200 text-yellow-800`;
      case TOAST_TYPES.INFO:
      default:
        return `${baseStyles} bg-blue-50 border border-blue-200 text-blue-800`;
    }
  };

  const getIcon = () => {
    const iconProps = { className: "h-5 w-5 mr-3 flex-shrink-0" };
    
    switch (type) {
      case TOAST_TYPES.SUCCESS:
        return <CheckCircle {...iconProps} className="h-5 w-5 mr-3 flex-shrink-0 text-green-600" />;
      case TOAST_TYPES.ERROR:
        return <AlertCircle {...iconProps} className="h-5 w-5 mr-3 flex-shrink-0 text-red-600" />;
      case TOAST_TYPES.WARNING:
        return <AlertCircle {...iconProps} className="h-5 w-5 mr-3 flex-shrink-0 text-yellow-600" />;
      case TOAST_TYPES.INFO:
      default:
        return <Info {...iconProps} className="h-5 w-5 mr-3 flex-shrink-0 text-blue-600" />;
    }
  };

  return (
    <div className={getToastStyles()}>
      {getIcon()}
      <div className="flex-1 text-sm font-medium">
        {message}
      </div>
      <button
        onClick={onRemove}
        className="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Custom Hook to use Toast
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { addToast, removeToast, removeAllToasts } = context;

  return {
    toast: {
      success: (message, duration) => addToast(message, TOAST_TYPES.SUCCESS, duration),
      error: (message, duration) => addToast(message, TOAST_TYPES.ERROR, duration),
      info: (message, duration) => addToast(message, TOAST_TYPES.INFO, duration),
      warning: (message, duration) => addToast(message, TOAST_TYPES.WARNING, duration)
    },
    removeToast,
    removeAllToasts
  };
};

export default ToastProvider;
